import { type FC } from "react";

import { Switch } from "@snapchat/snap-design-system";

import { GameLensCriteriaModal } from "../../../../lens/components/shared";
import { ButtonLink, Flexbox, LabelWithTooltip, NewTag } from "../../../../shared/components";
import { useToggleState } from "../../../../shared/hooks";

import styles from "./GameLensTogglePanel.module.css";
import {
    GameLensCriteriaLinkText,
    GameLensDescription,
    GameLensLabel,
    GameLensPreviewRequirement,
    GameLensReviewDescription,
    GameLensTooltip,
} from "./messages.generated";

interface GameLensTogglePanelProps {
    isGameUserProvided: boolean;
    onGameLensToggle: (isGameUserProvided: boolean) => void;
}

export const GameLensTogglePanel: FC<GameLensTogglePanelProps> = ({ isGameUserProvided, onGameLensToggle }) => {
    const [gameLensCriteriaModalProps, showGameLensCriteriaModal] = useToggleState();

    const criteriaLink = (
        <ButtonLink className={styles.criteriaButton} fontSize={14} onClick={showGameLensCriteriaModal}>
            <GameLensCriteriaLinkText />
        </ButtonLink>
    );

    return (
        <>
            <Flexbox className={styles.panel} data-testid="game-lens-toggle-panel">
                <Flexbox className={styles.panelContent} flex="1" flexDirection="column" gap="var(--spacing-2xs)">
                    <Flexbox
                        className={styles.panelHeader}
                        data-testid="game-lens-toggle-header"
                        alignItems="center"
                        flexWrap="wrap"
                        gap="var(--spacing-2xs)"
                    >
                        <LabelWithTooltip
                            dataTestId="game-lens-toggle-label-with-tooltip"
                            className={styles.panelLabel}
                            label={
                                <span className={styles.panelTitle}>
                                    <GameLensLabel />
                                </span>
                            }
                            content={<GameLensTooltip />}
                        />
                        <div data-testid="game-lens-toggle-new-tag" className={styles.panelNewTag}>
                            <NewTag />
                        </div>
                    </Flexbox>
                    <Flexbox className={styles.panelBody} flexDirection="column" gap="var(--spacing-md)">
                        <p className={styles.panelParagraph}>
                            <GameLensDescription />
                        </p>
                        <p className={styles.panelParagraph}>
                            <GameLensReviewDescription criteriaLink={criteriaLink} />
                        </p>
                        <p className={styles.panelParagraph}>
                            <GameLensPreviewRequirement />
                        </p>
                    </Flexbox>
                </Flexbox>
                <Switch
                    id="toggle-game-lens"
                    className={styles.panelSwitch}
                    data-testid="game-lens-toggle"
                    isChecked={isGameUserProvided}
                    onChange={onGameLensToggle}
                />
            </Flexbox>
            <GameLensCriteriaModal modalProps={gameLensCriteriaModalProps} />
        </>
    );
};
